/**
 * Mia Platform Data Service
 * Handles fetching tenants (organizations) and projects from Mia Platform
 */
import config from '../config.js'
import { getBffHeaders, buildBffUrl } from '../utils/apiHeaders.js'
import miaPlatformAuth from '../auth/MiaPlatformAuth.js'
import { getAuthorizedFetch } from '../utils/fetchRegistry'

const CATALOG_GROUP = 'ai.mia-platform.eu'
const CATALOG_VERSION = 'v1'

class MiaPlatformDataService {
  // ── Catalog helpers ─────────────────────────────────────────────────────

  /**
   * Save an item via PUT to the Catalog BFF.
   * @param {string} family  - Catalog family (e.g. 'ai-prompts')
   * @param {string} kind    - Item kind (e.g. 'ai-prompt')
   * @param {string} itemId  - Slug ID
   * @param {string} name    - Display name
   * @param {string} description
   * @param {string} tenantId
   * @param {Object} spec    - Domain data (maps to old `resources`)
   */
  async _saveCatalogItem (family, kind, itemId, name, description, tenantId, spec, resourceVersion) {
    const body = {
      apiVersion: `${CATALOG_GROUP}/${CATALOG_VERSION}`,
      kind,
      metadata: { name: itemId, title: name, description: description || '' },
      spec,
    }
    if (resourceVersion != null) {
      body.resourceVersion = resourceVersion
    }
    const response = await fetch(
      `${config.bffUrl}/api/catalog/${CATALOG_GROUP}/${CATALOG_VERSION}/items/${family}/${itemId}`,
      {
        method: 'PUT',
        headers: { ...getBffHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      }
    )
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to save ${kind}: ${response.statusText}`)
    }
    return response.json()
  }

  /** Delete an item via DELETE from the Catalog BFF. */
  async _deleteCatalogItem (family, itemId) {
    const response = await fetch(
      `${config.bffUrl}/api/catalog/${CATALOG_GROUP}/${CATALOG_VERSION}/items/${family}/${itemId}`,
      { method: 'DELETE', headers: getBffHeaders(), credentials: 'include' }
    )
    if (!response.ok && response.status !== 404) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to delete from ${family}: ${response.statusText}`)
    }
    return response.json().catch(() => ({}))
  }

  // ── End Catalog helpers ─────────────────────────────────────────────────

  /**
   * Fetch user's tenants (organizations/companies)
   * Requires user authentication via BFF
   * @returns {Promise<Array>} Array of tenant objects
   */
  async getTenants () {
    try {
      const response = await fetch(`${config.bffUrl}/api/mia-platform/tenants`, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch tenants: ${response.statusText}`)
      }

      const tenants = await response.json()
      return tenants
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform tenants:', error)
      throw error
    }
  }

  /**
   * Fetch templates for a tenant
   * Requires user authentication via BFF
   * @param {string} tenantId - Tenant ID to fetch templates for
   * @returns {Promise<Array>} Array of template objects
   */
  async getTemplates (tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      const url = buildBffUrl('/api/mia-platform/templates', { tenantId })
      const response = await fetch(url, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch templates: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch templates')
      }

      return data.templates || []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform templates:', error)
      throw error
    }
  }

  /**
   * Fetch the project blueprint for a tenant
   * The blueprint holds the tenant's `defaultTemplateId` and the full `templates` list.
   * Requires user authentication via BFF
   * @param {string} tenantId - Tenant ID to fetch the blueprint for
   * @returns {Promise<Object|null>} Blueprint object (or null)
   */
  async getProjectBlueprint (tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      const url = buildBffUrl('/api/mia-platform/project-blueprint', { tenantId })
      const response = await fetch(url, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch project blueprint: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch project blueprint')
      }

      return data.blueprint || null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform project blueprint:', error)
      throw error
    }
  }

  /**
   * Fetch projects, optionally filtered by tenant and search query
   * Requires user authentication via BFF
   * @param {string} [tenantId] - Optional tenant ID to filter projects
   * @param {string} [search] - Optional search query
   * @param {number} [page] - Page number for pagination (default: 1)
   * @param {number} [perPage] - Items per page (default: 50)
   * @returns {Promise<Object>} Object with data array and pagination metadata
   */
  async getProjects (tenantId = null, search = null, page = 1, perPage = 50) {
    try {
      // Build URL with query parameters
      const queryParams = {
        page,
        per_page: perPage
      }
      if (tenantId) {
        queryParams.tenantId = tenantId
      }
      if (search) {
        queryParams.search = search
      }

      // eslint-disable-next-line no-console
      console.log('📁 [Service] Fetching projects with parameters:', { tenantId, search, page, perPage })

      const url = buildBffUrl('/api/mia-platform/projects', queryParams)

      // eslint-disable-next-line no-console
      console.log('📁 [Service] Final URL:', url)

      const response = await fetch(url, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch projects: ${response.statusText}`)
      }

      const result = await response.json()

      // Check if response has pagination structure
      if (result.data && result.pagination) {
        return result
      }

      // Backward compatibility: if API returns array directly
      return {
        data: result,
        pagination: {
          page: 1,
          per_page: result.length,
          total: result.length,
          total_pages: 1,
          has_more: false
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform projects:', error)
      throw error
    }
  }

  /**
   * Get full project configuration including tenant info
   * Requires user authentication via BFF
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Full project configuration object
   */
  async getFullProjectConfiguration (projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required')
      }

      // eslint-disable-next-line no-console
      console.log('📥 [Service] Fetching full project configuration for:', projectId)

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/${projectId}/configuration`, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch project configuration: ${response.statusText}`)
      }

      const configuration = await response.json()

      // eslint-disable-next-line no-console
      console.log('✅ [Service] Full project configuration received:', {
        environments: configuration.environments?.length,
        technologies: configuration.info?.technologies?.length,
        links: Object.keys(configuration.links || {}).length
      })

      return configuration
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching full project configuration:', error)
      throw error
    }
  }

  /**
   * Format tenant data for dropdown display
   * @param {Object} tenant - Raw tenant object from API
   * @returns {Object} Formatted tenant object with id and name
   */
  formatTenant (tenant) {
    // Debug: log the raw tenant to see what fields are available
    // eslint-disable-next-line no-console
    console.log('🏢 [Service] Formatting tenant:', tenant)

    // The Mia Platform API returns 'companyId' as the tenant identifier
    const tenantId = tenant.companyId || tenant.tenantId || tenant._id || tenant.id

    // eslint-disable-next-line no-console
    console.log('🏢 [Service] Extracted tenant ID:', tenantId)

    return {
      id: tenantId,
      name: tenant.companyName || tenant.name || 'Unnamed Tenant',
      tenantId, // Keep for backward compatibility
      // Preserve original data for additional context
      raw: tenant
    }
  }

  /**
   * Format project data for dropdown display
   * @param {Object} project - Raw project object from API
   * @returns {Object} Formatted project object with id and name
   */
  formatProject (project) {
    return {
      id: project._id || project.projectId || project.id,
      name: project.name || 'Unnamed Project',
      description: project.description,
      tenantId: project.tenantId || project.companyId,
      thumbnail: project.thumbnail || project.image || project.imageUrl,
      environments: project.environments,
      consoleUrl: project.consoleUrl,
      repositoryUrl: project.repositoryUrl,
      documentationUrl: project.documentationUrl,
      team: project.team,
      // Preserve original data for additional context
      raw: project
    }
  }

  /**
   * Fetch revisions for a project
   * Requires user authentication via BFF
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of revision objects
   */
  async getRevisions (projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required')
      }

      const response = await fetch(`${config.bffUrl}/api/mia-platform/revisions/${projectId}`, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch revisions: ${response.statusText}`)
      }

      const revisions = await response.json()
      return revisions
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform revisions:', error)
      throw error
    }
  }

  /**
   * Format revision data for dropdown display
   * @param {Object} revision - Raw revision object from API
   * @returns {Object} Formatted revision object
   */
  formatRevision (revision) {
    return {
      id: revision.name,
      name: revision.name,
      isDefault: revision.isDefault || false,
      commit: revision.commit,
      // Preserve original data for additional context
      raw: revision
    }
  }

  /**
   * Fetch microservices (configuration) for a project revision
   * Note: BFF uses service account authentication, no user token required
   * @param {string} projectId - Project ID
   * @param {string} revisionId - Revision ID/name
   * @returns {Promise<Object>} Configuration object containing services
   */
  async getMicroservices (projectId, revisionId) {
    try {
      if (!projectId || !revisionId) {
        throw new Error('Project ID and Revision ID are required')
      }

      const response = await fetch(`${config.bffUrl}/api/mia-platform/microservices/${projectId}/${revisionId}`, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch microservices: ${response.statusText}`)
      }

      const configuration = await response.json()
      return configuration
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching Mia Platform microservices:', error)
      throw error
    }
  }

  /**
   * Format microservice data for display
   * @param {string} serviceName - Service name (key from services object)
   * @param {Object} service - Raw service object from API
   * @returns {Object} Formatted service object
   */
  formatMicroservice (serviceName, service) {
    // Log the raw service data to see what fields are available
    // eslint-disable-next-line no-console
    console.log('🔍 Raw service data for', serviceName, ':', service)
    // eslint-disable-next-line no-console
    console.log('🔍 Available keys:', Object.keys(service))
    // eslint-disable-next-line no-console
    console.log('🔍 ProviderId:', service.providerId)

    // Extract GitLab repository URL if present
    let repositoryUrl = null

    // Check various possible fields for repository URL (prioritize repoUrl which is the Mia Platform standard)
    if (service.repoUrl) {
      repositoryUrl = service.repoUrl
    } else if (service.sshUrl) {
      repositoryUrl = service.sshUrl
    } else if (service.repository) {
      repositoryUrl = service.repository
    } else if (service.repositoryUrl) {
      repositoryUrl = service.repositoryUrl
    } else if (service.sourceUrl) {
      repositoryUrl = service.sourceUrl
    } else if (service.gitUrl) {
      repositoryUrl = service.gitUrl
    } else if (service.git) {
      repositoryUrl = service.git
    } else if (service.providerId && service.providerId.includes('gitlab')) {
      // providerId format: gitlab/group/project or similar
      const parts = service.providerId.split('/')
      if (parts.length >= 3) {
        const group = parts[1]
        const project = parts[2]
        repositoryUrl = `https://gitlab.com/${group}/${project}.git`
      }
    } else if (service.providerId) {
      // For Mia Platform internal git: extract from providerId
      // Format might be like: git.tools.mia-platform.eu/path/to/repo
      repositoryUrl = service.providerId.startsWith('http') ?
        service.providerId :
        `https://${service.providerId}`
    } else if (service.pipelines && service.pipelines.gitlab) {
      // Check if there's a gitlab pipelines configuration
      repositoryUrl = service.pipelines.gitlab.repositoryUrl || service.pipelines.gitlab.projectUrl
    } else if (service.archiveUrl) {
      // For templates that have archiveUrl
      repositoryUrl = service.archiveUrl
    }

    // eslint-disable-next-line no-console
    console.log('📦 Extracted repository URL:', repositoryUrl)

    return {
      name: serviceName,
      type: service.type || 'custom',
      dockerImage: service.dockerImage,
      description: service.description,
      replicas: service.replicas,
      tags: service.tags || [],
      repositoryUrl,
      // Additional comprehensive configuration data
      serviceAccountName: service.serviceAccountName,
      logParser: service.logParser,
      environment: service.environment || [],
      annotations: service.annotations || [],
      labels: service.labels || [],
      resources: service.resources,
      containerPorts: service.containerPorts || [],
      terminationGracePeriodSeconds: service.terminationGracePeriodSeconds,
      configMaps: service.configMaps || [],
      secrets: service.secrets || [],
      advanced: service.advanced,
      createdAt: service.createdAt,
      updatedAt: service.updatedAt,
      // Preserve original data for additional context
      raw: service
    }
  }

  /**
   * Trigger deployment for a project
   * @param {string} projectId - Project ID
   * @param {Object} deployConfig - Deployment configuration
   * @param {string} deployConfig.deployType - Type of deployment (e.g., 'deploy_all')
   * @param {string} deployConfig.environment - Target environment (e.g., 'DEV', 'PROD')
   * @param {string} deployConfig.refType - Reference type ('revision' or 'tag')
   * @param {string} deployConfig.revision - Branch name or tag
   * @returns {Promise<Object>} Deployment result with pipelineId
   */
  async triggerDeployment (projectId, deployConfig) {
    try {
      // eslint-disable-next-line no-console
      console.log('🚀 [Service] Triggering deployment:', { projectId, deployConfig })

      // Get user access token for authorization
      const accessToken = localStorage.getItem('mia_flow_access_token')

      const headers = getBffHeaders({
        'Content-Type': 'application/json'
      })

      // Include user token if available
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
      }

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/${projectId}/deploy`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          deployType: 'deploy_all',
          environment: deployConfig.environment,
          refType: deployConfig.refType || 'revision',
          revision: deployConfig.revision
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to trigger deployment: ${response.statusText}`)
      }

      const result = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Deployment triggered:', result)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error triggering deployment:', error)
      throw error
    }
  }

  /**
   * Get deployment pipeline status
   * @param {string} projectId - Project ID
   * @param {string} pipelineId - Pipeline ID
   * @param {string} environment - Environment name
   * @returns {Promise<Object>} Pipeline status information
   */
  async getPipelineStatus (projectId, pipelineId, environment) {
    try {
      // eslint-disable-next-line no-console
      console.log('📊 [Service] Fetching pipeline status:', { projectId, pipelineId, environment })

      // Get user access token for authorization
      const accessToken = localStorage.getItem('mia_flow_access_token')

      const headers = getBffHeaders()

      // Include user token if available
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
      }

      // Build URL with environment query parameter
      const url = buildBffUrl(`/api/mia-platform/projects/${projectId}/pipelines/${pipelineId}/status`, {
        environment
      })

      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch pipeline status: ${response.statusText}`)
      }

      const status = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Pipeline status:', status)
      return status
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching pipeline status:', error)
      throw error
    }
  }

  /**
   * Get pods status for an environment
   * @param {string} projectId - Project ID
   * @param {string} environmentId - Environment ID
   * @returns {Promise<Array>} List of pods with their status
   */
  async getPodsStatus (projectId, environmentId) {
    try {
      // eslint-disable-next-line no-console
      console.log('📦 [Service] Fetching pods status:', { projectId, environmentId })

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/${projectId}/environments/${environmentId}/pods`, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch pods status: ${response.statusText}`)
      }

      const pods = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Pods status:', pods)
      return pods
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching pods status:', error)
      throw error
    }
  }

  /**
   * Get Mia Flow templates from marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} searchQuery - Search query (default: "mia flow")
   * @returns {Promise<Object>} Template list result
   */
  async getMiaFlowTemplates (tenantId, searchQuery = 'mia flow') {
    try {
      // eslint-disable-next-line no-console
      console.log('📦 [Service] Fetching Mia Flow templates:', { tenantId, searchQuery })

      const url = buildBffUrl('/api/mia-platform/marketplace/templates', {
        tenantId,
        search: searchQuery
      })

      const response = await fetch(url, {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch templates: ${response.statusText}`)
      }

      const result = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Templates fetched:', result)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching templates:', error)
      throw error
    }
  }

  /**
   * Create a new service from a marketplace template
   * @param {Object} params - Service creation parameters
   * @param {string} params.projectId - Project ID
   * @param {string} params.revisionId - Revision ID (branch name)
   * @param {string} params.serviceName - Name of the service
   * @param {string} params.serviceDescription - Service description
   * @param {string} params.templateId - Template ID (MongoDB ObjectId)
   * @param {string} params.templateItemId - Template item ID
   * @param {string} params.templateTenantId - Template tenant ID
   * @param {string} params.templateVersion - Template version
   * @returns {Promise<Object>} Service creation result
   */
  async createServiceFromTemplate (params) {
    try {
      // eslint-disable-next-line no-console
      console.log('🚀 [Service] Creating service from template:', params)

      const response = await fetch(`${config.bffUrl}/api/mia-platform/services/create`, {
        method: 'POST',
        headers: getBffHeaders({}, true), // Include Mia Platform user auth
        credentials: 'include',
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create service: ${response.statusText}`)
      }

      const result = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Service created:', result)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error creating service:', error)
      throw error
    }
  }

  /**
   * Create a new service from a Git repository
   * @param {Object} params - Service creation parameters
   * @param {string} params.projectId - Project ID
   * @param {string} params.revisionId - Revision ID
   * @param {string} params.serviceName - Service name
   * @param {string} params.serviceDescription - Service description
   * @param {string} params.gitProvider - Git provider (github, gitlab, azure, bitbucket)
   * @param {string} params.repositoryUrl - Repository URL
   * @param {string} params.dockerImage - Docker image name
   * @returns {Promise<Object>} Service creation result
   */
  async createServiceFromGit (params) {
    try {
      // eslint-disable-next-line no-console
      console.log('🚀 [Service] Creating service from Git repository:', params)

      const response = await fetch(`${config.bffUrl}/api/mia-platform/services/create-from-git`, {
        method: 'POST',
        headers: getBffHeaders({}, true), // Include Mia Platform user auth
        credentials: 'include',
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create service from Git: ${response.statusText}`)
      }

      const result = await response.json()
      // eslint-disable-next-line no-console
      console.log('✅ [Service] Service created from Git:', result)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error creating service from Git:', error)
      throw error
    }
  }

  /**
   * Create a new project from a template
   * @param {Object} params - Project creation parameters
   * @param {string} params.tenantId - Tenant ID
   * @param {string} params.projectName - Project name
   * @param {string} params.projectDescription - Project description (optional)
   * @param {string} params.templateId - Template ID (UUID)
   * @param {string} [params.providerId] - Git provider ID (optional)
   * @param {string} [params.gitProviderType] - Git provider type (optional)
   * @param {string} [params.repositoryVisibility] - Repository visibility (default: 'internal')
   * @param {string} [params.flavor] - Project flavor (default: 'application')
   * @returns {Promise<Object>} Project creation result with project object
   */
  async createProject (params) {
    try {
      // eslint-disable-next-line no-console
      console.log('🚀 [Service] Creating project:', params)

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/create`, {
        method: 'POST',
        headers: getBffHeaders({ 'Content-Type': 'application/json' }, true),
        credentials: 'include',
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create project: ${response.statusText}`)
      }

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to create project')
      }

      // eslint-disable-next-line no-console
      console.log('✅ [Service] Project created:', result.project)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error creating project:', error)
      throw error
    }
  }

  /**
   * Generate a project name from a prompt using AI
   * Note: BFF uses service account authentication, no user token required
   * @param {string} prompt - The project description/prompt
   * @param {string} [originalName] - Optional original name to improve upon
   * @returns {Promise<Object>} Object with suggestedName and originalName
   */
  async generateProjectName (prompt, originalName = null, playbookId = null, tenantId = null) {
    try {
      // eslint-disable-next-line no-console
      console.log('🤖 [Service] Generating project name from prompt:', { prompt: prompt.substring(0, 50), originalName, playbookId })

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projectnamefromprompt`, {
        method: 'POST',
        headers: getBffHeaders({ 'Content-Type': 'application/json' }, true),
        credentials: 'include',
        body: JSON.stringify({ prompt, originalName, playbookId, tenantId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to generate project name: ${response.statusText}`)
      }

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate project name')
      }

      // eslint-disable-next-line no-console
      console.log('✅ [Service] Project name generated:', {
        suggestedName: result.suggestedName,
        hasAiPrompt: !!result.aiPrompt,
        promptLength: result.aiPrompt?.length
      })
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error generating project name:', error)
      throw error
    }
  }

  /**
   * Create a project folder
   * Note: BFF uses service account authentication, no user token required
   * @param {string} name - Folder name
   * @param {string} [description] - Optional folder description
   * @returns {Promise<Object>} Created folder object
   */
  async createProjectFolder (name, description = '') {
    try {
      if (!name) {
        throw new Error('Folder name is required')
      }

      // eslint-disable-next-line no-console
      console.log('📁 [Service] Creating project folder:', { name, description })

      const response = await fetch(`${config.bffUrl}/api/mia-platform/folders`, {
        method: 'POST',
        headers: {
          ...getBffHeaders({}, true),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ name, description })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create folder: ${response.statusText}`)
      }

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to create folder')
      }

      // eslint-disable-next-line no-console
      console.log('✅ [Service] Folder created:', result.folder)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error creating folder:', error)
      throw error
    }
  }

  /**
   * Trigger deployment for a project environment
   * Note: BFF uses service account authentication, no user token required
   * @param {string} projectId - Project ID
   * @param {string} environment - Environment name (e.g., 'DEV', 'PROD')
   * @param {string} refType - Reference type ('revision' or 'version')
   * @param {string} revision - Revision or tag name (e.g., 'main', 'v1.0.0')
   * @returns {Promise<Object>} Deployment result with pipeline ID
   */
  async deployProject (projectId, environment, refType, revision) {
    try {
      if (!projectId || !environment || !refType || !revision) {
        throw new Error('All parameters are required: projectId, environment, refType, revision')
      }

      // eslint-disable-next-line no-console
      console.log('🚀 [Service] Triggering deployment:', { projectId, environment, refType, revision })

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/${projectId}/deploy`, {
        method: 'POST',
        headers: {
          ...getBffHeaders({}, true),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ environment, refType, revision })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to trigger deployment: ${response.statusText}`)
      }

      const result = await response.json()

      // Check for error in response (BFF returns {error: ...} on failure)
      if (result.error) {
        throw new Error(result.error || 'Failed to trigger deployment')
      }

      // eslint-disable-next-line no-console
      console.log('✅ [Service] Deployment triggered:', result)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error triggering deployment:', error)
      throw error
    }
  }

  /**
   * Save an AI prompt to the marketplace
   * @param {Object} promptData - Prompt data to save
   * @param {string} promptData.name - Prompt name
   * @param {string} promptData.description - Prompt description
   * @param {string} promptData.promptText - The actual prompt text
   * @param {string} promptData.goal - Prompt goal (optional)
   * @param {string} promptData.tenantId - Tenant ID
   * @returns {Promise<Object>} Saved prompt object
   */
  async saveAIPrompt (promptData) {
    try {
      const { name, description, promptText, goal, tenantId } = promptData

      if (!name || !description || !promptText || !tenantId) {
        throw new Error('Missing required fields: name, description, promptText, and tenantId are required')
      }

      // Generate itemId from name (lowercase, replace spaces with hyphens)
      const itemId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      return this._saveCatalogItem('prompts', 'Prompt', itemId, name, description, tenantId, { promptGoal: goal || description, promptText })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI prompt:', error)
      throw error
    }
  }

  /**
   * Save an AI message to the marketplace
   * @param {Object} messageData - Message data to save
   * @param {string} messageData.name - Message name
   * @param {string} messageData.description - Message description
   * @param {string} messageData.messageText - The actual message text
   * @param {string} messageData.category - Message category (optional)
   * @param {string} messageData.tenantId - Tenant ID
   * @returns {Promise<Object>} Saved message object
   */
  async saveAIMessage (messageData) {
    try {
      const { name, description, messageText, category, tenantId } = messageData

      if (!name || !description || !messageText || !tenantId) {
        throw new Error('Missing required fields: name, description, messageText, and tenantId are required')
      }

      // Generate itemId from name (lowercase, replace spaces with hyphens)
      const itemId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      return this._saveCatalogItem('prompts', 'Message', itemId, name, description, tenantId, { messageText, messageCategory: category || 'General' })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI message:', error)
      throw error
    }
  }

  /**
   * Delete an AI prompt from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId - Item ID to delete
   * @param {string} version - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAIPrompt (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('prompts', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI prompt:', error)
      throw error
    }
  }

  /**
   * Save or update an AI agent to the marketplace
   * @param {Object} agentData - Agent data to save
   * @param {string} agentData.name - Agent name
   * @param {string} agentData.description - Agent description
   * @param {string} agentData.agentDescription - Detailed agent description/instructions
   * @param {string} agentData.goal - Agent goal (optional)
   * @param {string} agentData.model - Agent model (optional)
   * @param {Array} agentData.tools - Agent tools (optional)
   * @param {string} agentData.tenantId - Tenant ID
   * @returns {Promise<Object>} Save result
   */
  async saveAIAgent (agentData) {
    try {
      const {
        name,
        runtime_name,
        description,
        instruction,
        model,
        tools,
        llm_args,
        tenantId
      } = agentData

      if (!name || !runtime_name || !description || !instruction || !tenantId) {
        throw new Error('Missing required fields: name, runtime_name, description, instruction, and tenantId are required')
      }

      // Generate itemId from name (lowercase, replace spaces with hyphens)
      const itemId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      // Construct resources based on the new schema
      const resources = {
        name,
        runtime_name,
        model: model || 'gemini-2.5-flash',
        instruction,
        description,
        tools: Array.isArray(tools) ? tools : [],
        llm_args: llm_args || {}
      }

      return this._saveCatalogItem('agents', 'Agent', itemId, name, description, tenantId, resources)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI agent:', error)
      throw error
    }
  }

  /**
   * Delete an AI agent from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId - Item ID to delete
   * @param {string} version - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAIAgent (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('agents', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI agent:', error)
      throw error
    }
  }

  /**
   * Save or update an Agentic Flow to the marketplace
   * @param {Object} flowData - Flow data to save
   * @param {string} flowData.name - Flow name
   * @param {string} flowData.description - Flow description
   * @param {string} flowData.tenantId - Tenant ID
   * @param {Object} flowData.resources - Flow resources (ui_tree, flow_definition)
   * @returns {Promise<Object>} Saved flow object
   */
  async saveAgenticFlow (flowData) {
    try {
      const { name, description, tenantId, resources } = flowData

      if (!name || !tenantId || !resources) {
        throw new Error('Missing required fields: name, tenantId, and resources are required')
      }

      // Use existing itemId if updating, otherwise generate from name
      const itemId = flowData.itemId || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      // Keep original resources structure
      const resourcesPayload = {
        ...resources
      }

      return this._saveCatalogItem('agenticflows', 'Agenticflow', itemId, name, description || `Agentic Flow ${name}`, tenantId, resourcesPayload)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving Agentic Flow:', error)
      throw error
    }
  }

  /**
   * Delete an Agentic Flow from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId - Item ID to delete
   * @param {string} version - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAgenticFlow (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('agenticflows', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting Agentic Flow:', error)
      throw error
    }
  }

  /**
   * Get AI models from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} search - Search query
   * @returns {Promise<Array>} Array of model objects
   */
  async getAIModels (tenantId, search = null) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      const params = new URLSearchParams({
        tenantId,
        page: '1',
        perPage: '100'
      })

      if (search) {
        params.append('search', search)
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/catalog/ai-models?${params.toString()}`, {
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch AI models: ${response.statusText}`)
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch AI models')
      }
      return result.data?.items || []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching AI models:', error)
      throw error
    }
  }

  /**
   * Save or update an AI model to the marketplace
   * @param {Object} modelData
   * @returns {Promise<Object>} Save result
   */
  async saveAIModel (modelData) {
    try {
      const { modelName, modelType, modelSecretVar, modelUrl, tenantId } = modelData
      if (!modelName || !modelType || !tenantId) {
        throw new Error('Missing required fields: modelName, modelType, and tenantId')
      }

      // Use existing itemId if updating, otherwise generate from name
      const itemId = modelData.itemId || modelName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      const modelSpec = { modelName, modelType, modelSecretVar, modelUrl }

      return this._saveCatalogItem('models', 'Model', itemId, modelName, `Model: ${modelType}`, tenantId, modelSpec)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI model:', error)
      throw error
    }
  }

  /**
   * Delete an AI model
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId - Item ID to delete
   * @param {string} version - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAIModel (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('models', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI model:', error)
      throw error
    }
  }

  /**
   * Save or update an AI Spec to the marketplace
   * @param {Object} specData - Spec data to save
   * @param {string} specData.name - Spec name (e.g. "speckit-plan")
   * @param {string} specData.description - Short description
   * @param {string} specData.command - Speckit command (e.g. "plan")
   * @param {string} specData.content - Full markdown instructions for the agent
   * @param {string} specData.tenantId - Tenant ID
   * @returns {Promise<Object>} Save result
   */
  async saveAISpec (specData) {
    try {
      const { name, description, command, content, tenantId } = specData

      if (!name || !description || !command || !content || !tenantId) {
        throw new Error('Missing required fields: name, description, command, content, and tenantId are required')
      }

      // Generate itemId from name (lowercase, replace spaces with hyphens)
      const itemId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      return this._saveCatalogItem('spectemplates', 'SpecTemplate', itemId, name, description, tenantId, { command, content })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI spec:', error)
      throw error
    }
  }

  /**
   * Delete an AI Spec from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId - Item ID to delete
   * @param {string} version - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAISpec (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('spectemplates', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI spec:', error)
      throw error
    }
  }

  // ─── AI Skills (Agent Skills – https://agentskills.io) ─────────────────────

  /**
   * Save (create or update) an AI Skill in the marketplace
   * @param {object} params
   * @param {string} params.name           - Display name for the marketplace item
   * @param {string} params.description    - Marketplace item description
   * @param {string} params.skillName      - Slug identifier (lowercase, hyphens)
   * @param {string} params.skillDescription - Discovery description shown to agents
   * @param {string} params.content        - Full SKILL.md markdown body
   * @param {string} [params.compatibility] - Comma-separated compatible agents/tools
   * @param {string} [params.allowedTools] - Space-delimited pre-approved tool names
   * @param {Array}  [params.handoffs]     - Agent handoff suggestions array
   * @param {string} params.tenantId       - Tenant ID
   * @returns {Promise<Object>} Save result
   */
  async saveAISkill ({ name, description, skillName, skillDescription, content,
    compatibility = '', allowedTools = '', handoffs = [], tenantId }) {
    try {
      if (!name || !description || !skillName || !skillDescription || !content || !tenantId) {
        throw new Error('Missing required fields: name, description, skillName, skillDescription, content, and tenantId are required')
      }

      const itemId = skillName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      const resources = {
        skillName,
        description: skillDescription,
        content,
      }

      if (compatibility) resources.compatibility = compatibility
      if (allowedTools) resources.allowedTools = allowedTools
      if (handoffs && handoffs.length > 0) {
        resources.handoffs = JSON.stringify(handoffs)
      }

      return this._saveCatalogItem('skills', 'Skill', itemId, name, description, tenantId, resources)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI skill:', error)
      throw error
    }
  }

  /**
   * Delete an AI Skill from the marketplace
   * @param {string} tenantId - Tenant ID
   * @param {string} itemId   - Item ID to delete
   * @param {string} version  - Version to delete (defaults to 'NA')
   * @returns {Promise<Object>} Delete result
   */
  async deleteAISkill (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('skills', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI skill:', error)
      throw error
    }
  }

  /**
   * Get user's favorite projects
   * Note: BFF uses service account authentication, no user token required
   * @param {string} [tenantId] - Optional tenant ID to filter favorites
   * @returns {Promise<Array>} Array of favorite project IDs or objects
   */
  async getFavorites (tenantId = null) {
    try {
      const queryParams = tenantId ? { tenantId } : {}
      const url = buildBffUrl('/api/mia-platform/user/favorites', queryParams)

      const response = await fetch(url, {
        method: 'GET',
        headers: getBffHeaders(null, true)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch favorites: ${response.statusText}`)
      }

      const result = await response.json()
      return result.favorites || []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching favorites:', error)
      throw error
    }
  }

  /**
   * Toggle project favorite status (add or remove)
   * Note: BFF uses service account authentication, no user token required
   * @param {string} projectId - Project ID to toggle
   * @returns {Promise<Object>} Result of toggle operation
   */
  async toggleFavorite (projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required')
      }

      const response = await fetch(`${config.bffUrl}/api/mia-platform/user/favorites`, {
        method: 'POST',
        headers: {
          ...getBffHeaders(null, true),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ _id: projectId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to toggle favorite: ${response.statusText}`)
      }

      const result = await response.json()
      return result.result || {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error toggling favorite:', error)
      throw error
    }
  }

  /**
   * Update project information (name, description, layerId, color, info)
   * @param {string} projectId - Project ID
   * @param {Object} updates - Object containing fields to update
   * @returns {Promise<Object>} Updated project object
   */
  async updateProject (tenantId, projectId, updates) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required')
      }

      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      const response = await fetch(`${config.bffUrl}/api/mia-platform/projects/${projectId}?tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'PATCH',
        headers: {
          ...getBffHeaders(null, true), // Include Mia Platform auth token
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to update project: ${response.statusText}`)
      }

      const result = await response.json()
      return result.project || {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error updating project:', error)
      throw error
    }
  }

  /**
   * Get folder assignments for projects
   * @param {string} tenantId - Tenant/organization ID
   * @param {Array<string>} projectIds - Optional array of project IDs to filter
   * @returns {Promise<Object>} Map of projectId -> folderId
   */
  async getProjectFolders (tenantId, projectIds = null) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      let url = `${config.bffUrl}/api/mia-platform/projects/folders?tenantId=${encodeURIComponent(tenantId)}`
      if (projectIds && projectIds.length > 0) {
        url += `&projectIds=${projectIds.join(',')}`
      }

      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to fetch project folders')
      }

      const result = await response.json()
      return result.folderMap || {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching project folders:', error)
      return {}
    }
  }

  /**
   * Get available technologies with categories
   * @returns {Promise<Object>} Object with technologies array and categories
   */
  async getTechnologies () {
    try {
      // Check session storage cache
      const cached = sessionStorage.getItem('mia_flow_technologies')
      if (cached) {
        try {
          return JSON.parse(cached)
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/templates/technologies`, {
        method: 'GET',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch technologies: ${response.statusText}`)
      }

      const result = await response.json()

      // Cache in session storage
      sessionStorage.setItem('mia_flow_technologies', JSON.stringify(result))

      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching technologies:', error)
      throw error
    }
  }

  /**
   * Get 1024-color palette
   * @returns {Promise<Object>} Object with colors array and grid info
   */
  async getColorPalette () {
    try {
      // Check session storage cache
      const cached = sessionStorage.getItem('mia_flow_color_palette')
      if (cached) {
        try {
          return JSON.parse(cached)
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/templates/colors`, {
        method: 'GET',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch color palette: ${response.statusText}`)
      }

      const result = await response.json()

      // Cache in session storage
      sessionStorage.setItem('mia_flow_color_palette', JSON.stringify(result))

      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching color palette:', error)
      throw error
    }
  }

  /**
   * Get all folders for a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of folder objects
   */
  async getFolders (tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required')
      }

      const url = buildBffUrl('/bff/folders', { tenantId })
      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch folders: ${response.statusText}`)
      }

      const result = await response.json()
      return result.folders || []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching folders:', error)
      throw error
    }
  }

  /**
   * Create a new folder
   * @param {string} tenantId - Tenant ID
   * @param {Object} folderData - Folder data (name, description, parentFolderId, color, icon)
   * @returns {Promise<Object>} Created folder object
   */
  async createFolder (tenantId, folderData) {
    try {
      if (!tenantId || !folderData.name) {
        throw new Error('Tenant ID and folder name are required')
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/folders`, {
        method: 'POST',
        headers: {
          ...getBffHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, ...folderData })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create folder: ${response.statusText}`)
      }

      const result = await response.json()
      return result.folder || {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error creating folder:', error)
      throw error
    }
  }

  /**
   * Update a folder
   * @param {string} tenantId - Tenant ID
   * @param {string} folderId - Folder ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated folder object
   */
  async updateFolder (tenantId, folderId, updates) {
    try {
      if (!tenantId || !folderId) {
        throw new Error('Tenant ID and folder ID are required')
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          ...getBffHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, ...updates })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to update folder: ${response.statusText}`)
      }

      const result = await response.json()
      return result.folder || {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error updating folder:', error)
      throw error
    }
  }

  /**
   * Delete a folder (only if empty)
   * @param {string} tenantId - Tenant ID
   * @param {string} folderId - Folder ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteFolder (tenantId, folderId) {
    try {
      if (!tenantId || !folderId) {
        throw new Error('Tenant ID and folder ID are required')
      }

      const url = buildBffUrl(`/bff/folders/${folderId}`, { tenantId })
      const response = await getAuthorizedFetch()(url, {
        method: 'DELETE',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        // Return specific error info for folder not empty
        if (errorData.code === 'FOLDER_NOT_EMPTY') {
          const error = new Error(errorData.error || 'Folder is not empty')
          error.code = 'FOLDER_NOT_EMPTY'
          error.details = errorData.details || {}
          throw error
        }

        throw new Error(errorData.error || `Failed to delete folder: ${response.statusText}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting folder:', error)
      throw error
    }
  }

  /**
   * Reorder folders within the same parent
   * @param {string} tenantId - Tenant ID
   * @param {string|null} parentFolderId - Parent folder ID (null for root)
   * @param {Array<string>} orderedFolderIds - Array of folder IDs in desired order
   * @returns {Promise<Object>} Reorder result
   */
  async reorderFolders (tenantId, parentFolderId, orderedFolderIds) {
    try {
      if (!tenantId || !Array.isArray(orderedFolderIds)) {
        throw new Error('Tenant ID and ordered folder IDs array are required')
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/folders/reorder`, {
        method: 'POST',
        headers: {
          ...getBffHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, parentFolderId, orderedFolderIds })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to reorder folders: ${response.statusText}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error reordering folders:', error)
      throw error
    }
  }

  /**
   * Get recent projects for the current user
   * @param {string} tenantId - Optional tenant ID to filter projects
   * @returns {Promise<Array>} Array of recent projects with visitedAt dates
   */
  async getRecentProjects (tenantId = null) {
    try {
      const url = new URL(`${config.bffUrl}/api/recent-projects`)
      if (tenantId) {
        url.searchParams.append('tenantId', tenantId)
      }

      const response = await getAuthorizedFetch()(url.toString(), {
        method: 'GET',
        headers: getBffHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to fetch recent projects: ${response.statusText}`)
      }

      const data = await response.json()
      return data.recentProjects || []
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching recent projects:', error)
      // Return empty array on error instead of throwing
      return []
    }
  }

  // ── AI Playbooks ─────────────────────────────────────────────────────────────

  /**
   * Save (create or update) an AI Playbook to the marketplace.
   * A playbook bundles agents, flows, prompts, specs, skills and tools
   * into a single named configuration guideline.
   *
   * @param {Object} playbookData
   * @param {string} playbookData.name         - Playbook display name
   * @param {string} playbookData.description  - Short description
   * @param {string} playbookData.tenantId     - Tenant ID
   * @param {Array}  playbookData.agents       - [{itemId, name}]
   * @param {Array}  playbookData.flows        - [{itemId, name}]
   * @param {Array}  playbookData.prompts      - [{itemId, name}]
   * @param {Array}  playbookData.specs        - [{itemId, name}]
   * @param {Array}  playbookData.skills       - [{itemId, name}]
   * @param {Array}  playbookData.tools        - [{itemId, name}] or string[]
   * @param {string} [playbookData.itemId]     - Existing itemId when updating
   * @returns {Promise<Object>}
   */
  async saveAIPlaybook (playbookData) {
    try {
      const { name, description, tenantId, agents = [], flows = [], prompts = [], specs = [], skills = [], tools = [] } = playbookData

      if (!name || !tenantId) {
        throw new Error('Missing required fields: name and tenantId are required')
      }

      const itemId = playbookData.itemId || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

      const resources = { name, description, agents, flows, prompts, specs, skills, tools }

      return this._saveCatalogItem('playbooks', 'Playbook', itemId, name, description || '', tenantId, resources)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error saving AI playbook:', error)
      throw error
    }
  }

  /**
   * Delete an AI Playbook from the marketplace.
   * @param {string} tenantId
   * @param {string} itemId
   * @param {string} [version='NA']
   * @returns {Promise<Object>}
   */
  async deleteAIPlaybook (tenantId, itemId, version = 'NA') {
    try {
      if (!tenantId || !itemId) {
        throw new Error('Missing required fields: tenantId and itemId are required')
      }

      return this._deleteCatalogItem('playbooks', itemId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting AI playbook:', error)
      throw error
    }
  }

  /**
   * Delete a project from Mia Platform
   * @param {string} projectId - Project ID to delete
   * @returns {Promise<Object>} Delete result
   */
  async deleteProject (projectId) {
    try {
      if (!projectId) {
        throw new Error('Project ID is required')
      }

      // eslint-disable-next-line no-console
      console.log('🗑️  [Service] Deleting project:', projectId)

      const response = await fetch(
        `${config.bffUrl}/api/mia-platform/projects/${projectId}`,
        {
          method: 'DELETE',
          headers: getBffHeaders({}, true),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to delete project: ${response.statusText}`)
      }

      const result = await response.json()
      if (result.error) throw new Error(result.error)
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error deleting project:', error)
      throw error
    }
  }
}

// Export singleton instance
const miaPlatformDataService = new MiaPlatformDataService()
export default miaPlatformDataService
